import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import CustomInputBox from "../../Form/CustomInputBox";
import { Numbers, Pause, Phone, PlayArrow } from "@mui/icons-material";
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import CustomButton from "../../Button/CustomButton";
import apiClient from "../../../services/apiClient";
import { LuTextCursorInput } from "react-icons/lu";
import { FaLanguage, FaSourcetree, FaUserTie } from "react-icons/fa6";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { AiOutlineCode } from "react-icons/ai";

const EditAIAgent = () => {
  const [formData, setFormData] = useState({
    agent_name: "",
    assigned_extension: "",
    assigned_number: "",
    request_status: "",
    personality: "",
    sales_script: "",
    assigned_voice_type: "",
    assigned_language_type: "",
    voice_source: "",
  });
  const [voiceData, setVoiceData] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [audio, setAudio] = useState(null);
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.userInfo?.user);
  const modal = useSelector((state) => state?.modal);
  const agentId = modal?.modal?.data?._id;
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [languageData, setLanguageData] = useState([]);
  const [voiceSource, setVoiceSource] = useState([
    { source_name: "Google", source_id: "google", model_name: "google_model" },
    { source_name: "Custom", source_id: "custom", model_name: "custom_model" },
  ]);

  // Fetch details from API instead of Redux dispatch
  useEffect(() => {
    const fetchAgentDetails = async () => {
      try {
        const response = await apiClient.get(`/agent-details-id/${agentId}`);
        const data = response.data;
        // console.log(data, "datatatattata");

        setFormData({
          agent_name: data?.agent_name || "",
          assigned_extension: data?.assigned_extension || "",
          assigned_number: data?.assigned_number || "",
          sales_script: data?.sales_script || "",
          assigned_voice_type: data?.assigned_voice_type?.id || "",
          personality: data?.personality,
          assigned_language_type: data?.assigned_language_type?.id || "",
          voice_source: data?.voice_source || "",
        });
      } catch (error) {
        console.error("Failed to fetch agent details:", error);
      }
    };

    if (agentId) fetchAgentDetails();
  }, [agentId]);

  const handleClosePopup = () => {
    dispatch(closeModal());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();


  const formDataToSend = new FormData();


  Object.entries(formData).forEach(([key, value]) => {
    formDataToSend.append(key, value);
  });


  formDataToSend.append("user_id", userInfo?.id);
  formDataToSend.append("company_id", userInfo?.company_id);

  setLoading(true);

  try {
    await apiClient.put(`/update-agent-details/${agentId}`, formDataToSend, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    dispatch(
      closeModalWithUpdate({
        isUpdated: {
          isUpdatedIn: "UPDATE_REQUEST",
          data: null,
        },
      })
    );
  } catch (error) {
    console.error("Error saving request:", error);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchVoice();
    fetchLanguage();
    fetchExtensions();
  }, []);

  const fetchVoice = async () => {
    try {
      const response = await apiClient.get("/voice-codes");
      setVoiceData(response?.data || []);
    } catch (error) {
      console.error("Voice fetch failed:", error);
    }
  };

  const fetchLanguage = async () => {
    try {
      const response = await apiClient.get("/languages");
      setLanguageData(response?.data || []);
    } catch (error) {
      console.error("Language fetch failed:", error);
    }
  };

  const fetchExtensions = async () => {
    try {
      const response = await apiClient.get(
        `/assignedPhoneDetails?owner_id=${userInfo?.id}&company_id=${userInfo?.company_id}&assigned_phone_status=not-assigned&page=1&page_size=100`
      );
      setExtensions(response?.data || []);
    } catch (error) {
      console.error("Error fetching extensions:", error);
    }
  };

  const handlePlay = (ele) => {
    if (audio) {
      audio.pause();
      setAudio(null);
      setPlayingId(null);
    }

    if (playingId !== ele._id) {
      const newAudio = new Audio(
        `https://dialgrid-api.greymatterz.com/${ele.voice_sample.replace(/\\/g, "/")}`
      );
      newAudio.play();
      setAudio(newAudio);
      setPlayingId(ele._id);

      newAudio.onended = () => {
        setPlayingId(null);
        setAudio(null);
      };
    }
  };

  return (
    <div className={"rightModal taskModal"}>
      <div className="modal-header p-3">
        <h4 className="m-0">Edit Agent Details</h4>
        <button className="btnClose ms-auto" onClick={handleClosePopup}>
          <IoCloseOutline />
        </button>
      </div>

      <div className="p-4">
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <div className="inputData withIcon mb-4">
              <span className="icon">
                <LuTextCursorInput />
              </span>
              <input
                className="form-control"
                type="text"
                name="agent_name"
                required
                value={formData?.agent_name}
                onChange={handleChange}
              />
              <label className="label">Agent Name</label>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon">
                <FaLanguage />
              </span>

              <FormControl fullWidth>
                <InputLabel>Choose AI Language</InputLabel>
                <Select
                  name="assigned_language_type"
                  value={formData.assigned_language_type || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_language_type: e.target.value,
                    }))
                  }
                  renderValue={(selected) => {
                    const language = languageData.find((v) => v._id === selected);
                    return language ? language.language_name : "Choose AI Language";
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {languageData.map((ele) => (
                    <MenuItem key={ele._id} value={ele._id}>
                      <ListItemText primary={ele.language_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose AI Voice</InputLabel>
                <Select
                  name="assigned_voice_type"
                  value={formData.assigned_voice_type || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_voice_type: e.target.value,
                    }))
                  }
                  renderValue={(selected) => {
                    const voice = voiceData.find((v) => v._id === selected);
                    return voice ? voice.gender : "Choose AI Voice";
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {voiceData
                    .filter((ele) => ele.language._id === formData.assigned_language_type)
                    .map((ele) => (
                      <MenuItem key={ele._id} value={ele._id}>
                        <ListItemText primary={ele.gender} />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlay(ele);
                          }}
                        >
                          {playingId === ele._id ? <Pause /> : <PlayArrow />}
                        </IconButton>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon">
                <FaSourcetree />
              </span>

              <FormControl fullWidth>
                <InputLabel>Choose Voice Model</InputLabel>
                <Select
                  name="voice_source"
                  value={formData.voice_source || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      voice_source: e.target.value,
                    }))
                  }
                  renderValue={(selected) => {
                    const tempvoiceSource = voiceSource.find((v) => v.source_id === selected);
                    return tempvoiceSource ? tempvoiceSource.source_name : "Choose Voice Model";
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {voiceSource.map((ele) => (
                    <MenuItem key={ele.source_id} value={ele.source_id}>
                      <ListItemText primary={ele.source_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose Extension</InputLabel>
                <Select
                  name="assigned_extension"
                  value={formData?.assigned_extension || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_extension: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {extensions?.map((ext, index) => (
                    <MenuItem key={index} value={ext?.data?.username}>
                      {ext?.data?.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose Number</InputLabel>
                <Select
                  name="assigned_number"
                  value={formData?.assigned_number || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_number: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {extensions?.map((ext, index) => (
                    <MenuItem key={index} value={ext?.data?.effective_caller_id_number}>
                      {ext?.data?.effective_caller_id_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div class="inputData textareaData withIcon mb-4">
              <span className="icon">
                <FaUserTie />
              </span>
              <textarea
                className="form-control h-auto"
                rows="5"
                cols="80"
                required
                name="personality"
                value={formData.personality}
                onChange={handleChange}
              ></textarea>
              <label className="label">Personality Define</label>
              <div class="needHelp text-end">
                Need Help{" "}
                <Tooltip
                  disableFocusListener
                  title="This is for creating the ai agent for dialgrid. It can only be created in dialgrid from admin."
                >
                  <span className="ms-1">
                    <BsFillQuestionCircleFill />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div class="inputData textareaData withIcon mb-4">
              <span className="icon">
                <AiOutlineCode />
              </span>
              <textarea
                className="form-control h-auto"
                rows="5"
                cols="80"
                required
                name="sales_script"
                value={formData.sales_script}
                onChange={handleChange}
              ></textarea>
              <label className="label">Sales Script</label>
              <div class="needHelp text-end">
                Need Help{" "}
                <Tooltip
                  disableFocusListener
                  title="This is for creating the ai agent for dialgrid. It can only be created in dialgrid from admin."
                >
                  <span className="ms-1">
                    <BsFillQuestionCircleFill />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mb-4">
              <CustomButton variant="contained" color="secondary" onClick={handleClosePopup}>
                Cancel
              </CustomButton>
              <CustomButton type="submit" variant="contained" color="primary" loading={loading}>
                Update
              </CustomButton>
            </div>
          </Stack>
        </Box>
      </div>
    </div>
  );
};

export default EditAIAgent;
